import React, { useEffect, useState } from "react";
import NavigationDrawer from "./components/NavigationDrawer";
import { Outlet, useLocation } from "react-router-dom";
import { RootState, useAppSelector } from "@Store/store";
import ReactModal from "react-modal";
import {
  DashboardActive,
  DashboardInactive,
  ContactsActive,
  ContactsInactive,
  AutomationsActive,
  AutomationsInactive,
  SettingsInactive,
  SettingsActive,
  ReferralActive,
  ReferralInactive,
  InboxActive,
  InboxInactive,
} from "@assets/index";
import PaymentOptions from "@Pages/PaymentOption";
import { XMarkIcon } from "@heroicons/react/24/solid";
import posthog from "posthog-js";
import { AnalyticsParamBuilder, clientId, redirectPath, responseType, scope } from "@Utils/common";

const navigationItems = [
  {
    to: "/dashboard",
    label: "Dashboard",
    activeIcon: DashboardActive,
    inactiveIcon: DashboardInactive,
    end: true,
  },
  {
    to: "automations",
    label: "Automations",
    activeIcon: AutomationsActive,
    inactiveIcon: AutomationsInactive,
  },
  {
    to: 'inbox',
    label: 'Inbox',
    activeIcon: InboxActive,
    inactiveIcon: InboxInactive
  },
  {
    to: "contacts",
    label: "Contacts",
    activeIcon: ContactsActive,
    inactiveIcon: ContactsInactive,
  },
  // {
  //   to: '/dashboard/schedule',
  //   label: 'Schedule Content',
  //   activeIcon: SchedulerActive,
  //   inactiveIcon: SchedulerInactive
  // },
  // {
  //   to: '/dashboard/comments',
  //   label: 'Manage Comments',
  //   activeIcon: CommentsActive,
  //   inactiveIcon: CommentsInactive
  // }
  {
    to: "referral",
    label: "Refer and Earn",
    activeIcon: ReferralActive,
    inactiveIcon: ReferralInactive,
    isNew: true,
  },
  {
    to: "settings",
    label: "Settings",
    activeIcon: SettingsActive,
    inactiveIcon: SettingsInactive,
  },
];

const COMPONENT = "dashboard_layout";

const DashboardLayout: React.FC = () => {
  useEffect(() => {
    document.title = "LinkPlease - Dashboard";
  }, []);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const location = useLocation();

  // Find matching navigation item
  const currentNavItem =
    navigationItems.find((item) => {
      // For exact dashboard route
      if (item.end && location.pathname === item.to) return true;
      // For nested routes like /dashboard/contacts
      return location.pathname.startsWith(`/dashboard/${item.to}`);
    }) || navigationItems[0]; // fallback to Dashboard

  const { user } = useAppSelector((state: RootState) => state.profile);
  const selectedIgData = useAppSelector((state: RootState) => state.igSelection.selectedIgData);

  const [igStatus, setigStatus] = useState(false);

  const [UpgradeToPro, setUpgradeToPro] = useState(false);

  // Construct the redirect URL dynamically using the current origin
  const redirectUri = `${window.location.origin}${redirectPath}`;

  useEffect(() => {
    if (!selectedIgData?.status) setigStatus(true);
    // if (!user?.ig_ids?.[0]?.status) setigStatus(true)
  }, [user]);

  const handleClickLoginButton = () => {
    posthog.capture(
      "ui_action",
      AnalyticsParamBuilder({
        kingdom: COMPONENT,
        phylum: "redirect",
        classValue: "reconnect_ig",
        genus: igStatus,
      })
    );
    const authUrl = `https://www.instagram.com/oauth/authorize?force_reauth=true&client_id=${clientId}&redirect_uri=${encodeURIComponent(redirectUri)}&response_type=${responseType}&scope=${encodeURIComponent(scope)}`;

    // Redirect the user to the dynamically constructed URL
    window.location.href = authUrl;
  };

  useEffect(() => {
    if (UpgradeToPro) {
      posthog.capture(
        "ui_view",
        AnalyticsParamBuilder({
          kingdom: COMPONENT,
          phylum: "modal",
          classValue: "upgrade_to_pro_modal",
        })
      );
    }
  }, [UpgradeToPro]);

  useEffect(() => {
    if (igStatus) {
      posthog.capture(
        "ui_view",
        AnalyticsParamBuilder({
          kingdom: COMPONENT,
          phylum: "modal",
          classValue: "reconnect_ig",
          genus: igStatus,
        })
      );
    }
  }, [igStatus]);

  useEffect(() => {
    if (isMobileMenuOpen) {
      posthog.capture(
        "ui_view",
        AnalyticsParamBuilder({
          kingdom: COMPONENT,
          phylum: "menu",
          classValue: "mobile_menu",
          genus: isMobileMenuOpen,
        })
      );
    }
  }, [isMobileMenuOpen]);

  const OnMobileUiClose = () => {
    posthog.capture(
      "ui_action",
      AnalyticsParamBuilder({
        kingdom: COMPONENT,
        phylum: "menu",
        classValue: "mobile_menu",
        genus: isMobileMenuOpen,
      })
    );
    setIsMobileMenuOpen(false);
    posthog.capture(
      "ui_close",
      AnalyticsParamBuilder({
        kingdom: COMPONENT,
        phylum: "menu",
        classValue: "mobile_menu",
      })
    );
  };

  const OnHamburgerClick = () => {
    posthog.capture(
      "ui_action",
      AnalyticsParamBuilder({
        kingdom: COMPONENT,
        phylum: "hamburger_menu",
      })
    );
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  return (
    <div className="flex h-screen bg-[#F5F4FA] relative overflow-hidden">
      {/* Mobile Navigation Header */}
      <div className="lg:hidden w-full bg-white fixed top-0 left-0 px-4 py-3 shadow-sm flex items-center justify-between z-10">
        {/* Left: Icon + Title */}
        <div className="flex items-center gap-2">
          {/* Example page icon — you can pass this dynamically */}
          {currentNavItem && (
            <>
              <img
                src={currentNavItem.activeIcon}
                alt={currentNavItem.label}
                className="w-5 h-5"
              />
              <span className="text-lg font-semibold text-gray-900">
                {currentNavItem.label}
              </span>
            </>
          )}
        </div>

        {/* Right: Hamburger Button */}
        <button
          onClick={OnHamburgerClick}
          className="p-2 hover:bg-gray-100 rounded-lg"
        >
          <svg
            className="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 6h16M4 12h16M4 18h16"
            />
          </svg>
        </button>
      </div>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div
          className="z-[60] fixed inset-0 bg-black bg-opacity-50  lg:hidden"
          onClick={OnMobileUiClose}
        />
      )}

      {/* Navigation Drawer - Hidden on mobile by default */}
      <div
        className={`z-[60] fixed lg:relative lg:flex h-full bg-white transform transition-transform duration-300 ease-in-out ${isMobileMenuOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}
      >
        <NavigationDrawer
          navigationItems={navigationItems}
          setIsMobileMenuOpen={setIsMobileMenuOpen}
          setUpgradeToPro={setUpgradeToPro}
        />
      </div>

      {/* Main Content Area */}
      <main className="flex-1 overflow-auto mt-16 lg:mt-0">
        <Outlet context={{ setUpgradeToPro }} />
      </main>
      <ReactModal
        isOpen={igStatus}
        onRequestClose={() => { }}
        className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-gray-100 rounded-xl  p-6 w-[95%] max-w-[500px]  focus:outline-none"
        overlayClassName="z-50 fixed inset-0 bg-black bg-opacity-50"
      >
        <div className="py-6 w-full max-w-md mx-auto">
          {/* Title */}
          <h1 className="text-2xl sm:text-3xl text-center text-red-600 font-semibold mb-3">
            Your automations are not working!
          </h1>

          {/* Subtitle */}
          <p className="text-center text-lg sm:text-xl font-medium text-gray-800 mb-6 break-words">
            Refresh Permissions for{" "}
            <span className="font-bold">@{selectedIgData?.username}</span>
          </p>

          {/* Reasons Box */}
          <div className="bg-white rounded-xl p-5 shadow-md mb-6">
            <p className="text-base sm:text-lg font-semibold text-gray-800 mb-4">
              It can be due to any of the following reasons:
            </p>

            <ul className="space-y-3">
              {[
                "You changed your Instagram password",
                "Incomplete Permission given",
                "Your token expired",
                "You removed us from Instagram Permissions by mistake",
              ].map((item, idx) => (
                <li key={idx} className="flex items-start gap-3">
                  <span className="text-red-500">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                      className="size-6 text-red-600"
                    >
                      <path
                        fill-rule="evenodd"
                        d="M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25Zm-1.72 6.97a.75.75 0 1 0-1.06 1.06L10.94 12l-1.72 1.72a.75.75 0 1 0 1.06 1.06L12 13.06l1.72 1.72a.75.75 0 1 0 1.06-1.06L13.06 12l1.72-1.72a.75.75 0 1 0-1.06-1.06L12 10.94l-1.72-1.72Z"
                        clip-rule="evenodd"
                      />
                    </svg>
                  </span>
                  <span className="text-sm sm:text-base text-gray-600">
                    {item}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          {/* Reconnect Button */}
          <div className="w-full">
            <button
              onClick={handleClickLoginButton}
              className="w-full bg-gradient-to-r from-[#833ab4] via-[#fd1d1d] to-[#fcb045] text-white py-3 rounded-lg font-semibold hover:opacity-90 transition-all flex items-center justify-center gap-3"
            >
              {/* Instagram Icon */}
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="white"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M12 2C14.717 2 15.056 2.01 16.122 2.06C17.187 2.11 17.912 2.277 18.55 2.525C19.21 2.779 19.766 3.123 20.322 3.678C20.8305 4.1779 21.224 4.78259 21.475 5.45C21.722 6.087 21.89 6.813 21.94 7.878C21.987 8.944 22 9.283 22 12C22 14.717 21.99 15.056 21.94 16.122C21.89 17.187 21.722 17.912 21.475 18.55C21.2247 19.2178 20.8311 19.8226 20.322 20.322C19.822 20.8303 19.2173 21.2238 18.55 21.475C17.913 21.722 17.187 21.89 16.122 21.94C15.056 21.987 14.717 22 12 22C9.283 22 8.944 21.99 7.878 21.94C6.813 21.89 6.088 21.722 5.45 21.475C4.78233 21.2245 4.17753 20.8309 3.678 20.322C3.16941 19.8222 2.77593 19.2175 2.525 18.55C2.277 17.913 2.11 17.187 2.06 16.122C2.013 15.056 2 14.717 2 12C2 9.283 2.01 8.944 2.06 7.878C2.11 6.812 2.277 6.088 2.525 5.45C2.77524 4.78218 3.1688 4.17732 3.678 3.678C4.17767 3.16923 4.78243 2.77573 5.45 2.525C6.088 2.277 6.812 2.11 7.878 2.06C8.944 2.013 9.283 2 12 2ZM12 7C10.6739 7 9.40215 7.52678 8.46447 8.46447C7.52678 9.40215 7 10.6739 7 12C7 13.3261 7.52678 14.5979 8.46447 15.5355C9.40215 16.4732 10.6739 17 12 17C13.3261 17 14.5979 16.4732 15.5355 15.5355C16.4732 14.5979 17 13.3261 17 12C17 10.6739 16.4732 9.40215 15.5355 8.46447C14.5979 7.52678 13.3261 7 12 7ZM18.5 6.75C18.5 6.41848 18.3683 6.10054 18.1339 5.86612C17.8995 5.6317 17.5815 5.5 17.25 5.5C16.9185 5.5 16.6005 5.6317 16.3661 5.86612C16.1317 6.10054 16 6.41848 16 6.75C16 7.08152 16.1317 7.39946 16.3661 7.63388C16.6005 7.8683 16.9185 8 17.25 8C17.5815 8 17.8995 7.8683 18.1339 7.63388C18.3683 7.39946 18.5 7.08152 18.5 6.75ZM12 9C12.7956 9 13.5587 9.31607 14.1213 9.87868C14.6839 10.4413 15 11.2044 15 12C15 12.7956 14.6839 13.5587 14.1213 14.1213C13.5587 14.6839 12.7956 15 12 15C11.2044 15 10.4413 14.6839 9.87868 14.1213C9.31607 13.5587 9 12.7956 9 12C9 11.2044 9.31607 10.4413 9.87868 9.87868C10.4413 9.31607 11.2044 9 12 9Z" />
              </svg>
              <span className="text-base sm:text-lg">Reconnect Instagram</span>
            </button>
          </div>
        </div>
      </ReactModal>

      <ReactModal
        isOpen={UpgradeToPro}
        onRequestClose={() => {
          setUpgradeToPro(false);
        }}
        className="md:absolute md:top-1/2 md:left-1/2 md:transform md:-translate-x-1/2 md:-translate-y-1/2 bg-black md:rounded-xl md:w-[90%] md:max-w-[1000px] md:h-auto md:max-h-[80vh] w-full h-full max-h-full focus:outline-none overflow-y-auto "
        overlayClassName="z-50 fixed inset-0 bg-black bg-opacity-80"
      >
        <button
          onClick={() => {
            setUpgradeToPro(false);
          }}
          className="sticky top-2 left-full -ml-10 z-10 rounded-full p-2 bg-white/10 hover:bg-white/20 text-white backdrop-blur-sm transition-colors"
        >
          <XMarkIcon className="size-5" />
        </button>
        <PaymentOptions
          signupFlow={false}
          closeModal={() => setUpgradeToPro(false)}
        />
      </ReactModal>
    </div>
  );
};

export default DashboardLayout;
